import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Shield, CheckCircle, Clock, AlertTriangle, Camera, FileText, 
  User, Loader2, ExternalLink, RefreshCw, ArrowRight, Upload,
  Eye, Cpu, Building2, X, Video, AlertCircle, Fingerprint
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
];

const DOCUMENT_TYPES = [
  { value: 'national_id', label: 'National ID Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's License" },
];

const INTERNAL_VERIFICATION_STEPS = [
  { id: 1, title: 'Upload ID', description: 'Upload your government-issued ID', icon: FileText },
  { id: 2, title: 'Video Liveness', description: 'Record a short video', icon: Camera },
  { id: 3, title: 'AI Verification', description: 'Our AI verifies your identity', icon: Cpu },
  { id: 4, title: 'Complete', description: 'Verification result', icon: Shield },
];

const ENHANCED_VERIFICATION_STEPS = [
  { id: 1, title: 'Upload ID', description: 'Upload your government-issued ID', icon: FileText },
  { id: 2, title: 'Fraud Check', description: 'AI scans for tampering', icon: Shield },
  { id: 3, title: 'Video Liveness', description: 'Record a liveness video', icon: Camera },
  { id: 4, title: 'AI Verification', description: 'Complete identity check', icon: Cpu },
  { id: 5, title: 'Complete', description: 'Verification result', icon: CheckCircle },
];

const VERIFF_STEPS = [
  { id: 1, title: 'Select Document', description: 'Choose your ID document country', icon: FileText },
  { id: 2, title: 'Verify with Veriff', description: 'Complete verification in Veriff', icon: ExternalLink },
  { id: 3, title: 'Review', description: 'Veriff reviews your submission', icon: Clock },
  { id: 4, title: 'Complete', description: 'Verification result', icon: Shield },
];

export default function KYCPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [kycStatus, setKycStatus] = useState('not_started');
  const [kycProvider, setKycProvider] = useState('internal');
  const [selectedCountry, setSelectedCountry] = useState('KE');
  const [documentType, setDocumentType] = useState('national_id');
  const [currentStep, setCurrentStep] = useState(1);
  
  // Veriff state
  const [veriffSession, setVeriffSession] = useState(null);
  
  // Internal KYC state
  const [idImage, setIdImage] = useState(null);
  const [idImagePreview, setIdImagePreview] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Enhanced verification state
  const [verificationMode, setVerificationMode] = useState('enhanced'); // 'standard' or 'enhanced'
  const [fraudResult, setFraudResult] = useState(null);
  const [videoFrames, setVideoFrames] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [livenessChallenge, setLivenessChallenge] = useState(null);
  const [challenges, setChallenges] = useState([]);
  
  const idInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const videoRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  const token = localStorage.getItem('onepay_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchKYCProvider();
    checkKYCStatus();
    fetchChallenges();
  }, [token, navigate]);

  const fetchKYCProvider = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/provider`);
      setKycProvider(response.data.provider);
    } catch (error) {
      console.error('Error fetching KYC provider:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/challenges`);
      setChallenges(response.data.challenges || []);
      // Set random challenge for enhanced mode
      const randomChallenge = response.data.challenges[Math.floor(Math.random() * response.data.challenges.length)];
      setLivenessChallenge(randomChallenge);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const checkKYCStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.kyc_verified) {
        setKycStatus('approved');
        setCurrentStep(4);
      }
    } catch (error) {
      console.error('Error checking KYC status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  // ============= INTERNAL KYC FUNCTIONS =============
  
  const handleIdImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: 'Error', description: 'Image must be less than 10MB', variant: 'destructive' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setIdImage(base64);
        setIdImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelfieUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setSelfieImage(base64);
        setSelfiePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (error) {
      toast({ title: 'Camera Error', description: 'Could not access camera', variant: 'destructive' });
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      const base64 = dataUrl.split(',')[1];
      setSelfieImage(base64);
      setSelfiePreview(dataUrl);
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    setIsRecording(false);
  };

  // ============= ENHANCED VERIFICATION FUNCTIONS =============
  
  const startVideoRecording = async () => {
    if (!videoRef.current || !videoRef.current.srcObject) {
      await startCamera();
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for camera to start
    }
    
    setVideoFrames([]);
    setIsRecording(true);
    setRecordingProgress(0);
    
    const duration = livenessChallenge?.duration_seconds || 5;
    const frameInterval = 500; // Capture every 500ms
    const totalFrames = (duration * 1000) / frameInterval;
    let frameCount = 0;
    
    recordingIntervalRef.current = setInterval(() => {
      if (videoRef.current) {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
        
        const base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        setVideoFrames(prev => [...prev, base64]);
        
        frameCount++;
        setRecordingProgress((frameCount / totalFrames) * 100);
        
        if (frameCount >= totalFrames) {
          clearInterval(recordingIntervalRef.current);
          setIsRecording(false);
          toast({ title: 'Recording Complete', description: 'Video captured successfully' });
        }
      }
    }, frameInterval);
  };

  const detectDocumentFraud = async () => {
    if (!idImage) {
      toast({ title: 'Error', description: 'Please upload your ID document first', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/kyc/internal/fraud-detection`, {
        document_image_base64: idImage,
        document_type: documentType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFraudResult(response.data.fraud_analysis);
        
        if (response.data.fraud_analysis.tampering_detected || response.data.fraud_analysis.risk_score > 0.5) {
          toast({ 
            title: 'Document Issue Detected', 
            description: 'Please use a different document', 
            variant: 'destructive' 
          });
        } else {
          setCurrentStep(3);
          toast({ title: 'Document Verified', description: 'Document appears authentic. Please record liveness video.' });
        }
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to analyze document', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const performEnhancedVerification = async () => {
    if (!idImage || videoFrames.length < 3) {
      toast({ title: 'Error', description: 'Please complete all steps first', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    setCurrentStep(4);
    
    try {
      const response = await axios.post(`${API_URL}/api/kyc/internal/enhanced-verify`, {
        id_image_base64: idImage,
        video_frames_base64: videoFrames,
        document_type: documentType,
        document_country: selectedCountry,
        challenge_type: livenessChallenge?.type || 'blink'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setVerificationResult(response.data);
        setCurrentStep(5);
        
        if (response.data.status === 'approved') {
          setKycStatus('approved');
          toast({ title: 'Verified!', description: 'Your identity has been fully verified' });
        } else if (response.data.status === 'manual_review') {
          setKycStatus('pending');
          toast({ title: 'Under Review', description: 'Your verification needs manual review' });
        } else {
          setKycStatus('rejected');
          toast({ title: 'Verification Failed', description: 'Please try again with clearer documents', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Verification failed', 
        variant: 'destructive' 
      });
      setCurrentStep(3);
    } finally {
      setLoading(false);
    }
  };

  const extractDocumentData = async () => {
    if (!idImage) {
      toast({ title: 'Error', description: 'Please upload your ID document first', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/kyc/internal/extract-document`, {
        id_image_base64: idImage,
        document_type: documentType,
        document_country: selectedCountry
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setExtractedData(response.data);
        setCurrentStep(2);
        toast({ title: 'Document Scanned', description: 'Please proceed to take a selfie' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to process document', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const performFullVerification = async () => {
    if (!idImage || !selfieImage) {
      toast({ title: 'Error', description: 'Please complete all steps first', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    setCurrentStep(3);
    
    try {
      const response = await axios.post(`${API_URL}/api/kyc/internal/verify`, {
        id_image_base64: idImage,
        selfie_base64: selfieImage,
        document_type: documentType,
        document_country: selectedCountry
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setVerificationResult(response.data);
        setCurrentStep(4);
        
        if (response.data.status === 'approved') {
          setKycStatus('approved');
          toast({ title: 'Verified!', description: 'Your identity has been verified' });
        } else if (response.data.status === 'manual_review') {
          setKycStatus('pending');
          toast({ title: 'Under Review', description: 'Your verification is being reviewed' });
        } else {
          setKycStatus('rejected');
          toast({ title: 'Verification Failed', description: 'Please try again with clearer documents', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Verification failed', 
        variant: 'destructive' 
      });
      setCurrentStep(2);
    } finally {
      setLoading(false);
    }
  };

  // ============= VERIFF FUNCTIONS =============
  
  const startVeriffSession = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/kyc/veriff/session`, {
        document_country: selectedCountry
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setVeriffSession(response.data);
        setCurrentStep(2);
        
        if (response.data.session_url) {
          window.open(response.data.session_url, '_blank', 'width=600,height=700');
        }
        
        toast({ title: 'Verification Started', description: 'Complete the verification in the new window' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to start verification', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const checkVeriffStatus = async () => {
    if (!veriffSession?.session_id) return;
    
    setCheckingStatus(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/kyc/veriff/status/${veriffSession.session_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'approved') {
        setKycStatus('approved');
        setCurrentStep(4);
        toast({ title: 'Verified!', description: 'Your identity has been verified' });
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  // ============= RENDER HELPERS =============
  
  const steps = kycProvider === 'internal' 
    ? (verificationMode === 'enhanced' ? ENHANCED_VERIFICATION_STEPS : INTERNAL_VERIFICATION_STEPS)
    : VERIFF_STEPS;

  const getStatusIcon = () => {
    switch (kycStatus) {
      case 'approved': return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'pending': return <Clock className="w-16 h-16 text-yellow-500" />;
      case 'rejected': return <AlertTriangle className="w-16 h-16 text-red-500" />;
      default: return <Shield className="w-16 h-16 text-teal-500" />;
    }
  };

  const resetVerification = () => {
    setCurrentStep(1);
    setIdImage(null);
    setIdImagePreview(null);
    setSelfieImage(null);
    setSelfiePreview(null);
    setExtractedData(null);
    setVerificationResult(null);
    setVeriffSession(null);
    setFraudResult(null);
    setVideoFrames([]);
    setIsRecording(false);
    setRecordingProgress(0);
    setKycStatus('not_started');
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="kyc-page">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold">Identity Verification</h1>
          <p className="text-muted-foreground">Secure your account with KYC verification</p>
        </div>

        {/* Provider Selection */}
        <Card className="border-teal-500/20">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                    {kycProvider === 'internal' ? <Cpu className="w-5 h-5 text-teal-500" /> : <Building2 className="w-5 h-5 text-teal-500" />}
                  </div>
                  <div>
                    <p className="font-medium">
                      {kycProvider === 'internal' 
                        ? (verificationMode === 'enhanced' ? 'OnePay Enhanced AI Verification' : 'OnePay AI Verification')
                        : 'Veriff Verification'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {kycProvider === 'internal' 
                        ? (verificationMode === 'enhanced' 
                            ? 'Video liveness + fraud detection + face matching' 
                            : 'Document OCR + face matching')
                        : 'Third-party identity verification'}
                    </p>
                  </div>
                </div>
                
                <Tabs value={kycProvider} onValueChange={setKycProvider} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="internal" className="flex items-center gap-2">
                      <Cpu className="w-4 h-4" />
                      Internal
                    </TabsTrigger>
                    <TabsTrigger value="veriff" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Veriff
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              {/* Enhanced Mode Toggle - only for internal */}
              {kycProvider === 'internal' && (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Enhanced Security Mode</p>
                    <p className="text-xs text-muted-foreground">Video liveness detection + document fraud analysis</p>
                  </div>
                  <Tabs value={verificationMode} onValueChange={setVerificationMode} className="w-auto">
                    <TabsList className="h-8">
                      <TabsTrigger value="standard" className="text-xs px-3 h-6">Standard</TabsTrigger>
                      <TabsTrigger value="enhanced" className="text-xs px-3 h-6">Enhanced</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center transition-colors
                        ${isCompleted ? 'bg-teal-500 text-white' : 
                          isActive ? 'bg-teal-500/20 text-teal-500 ring-2 ring-teal-500' : 
                          'bg-muted text-muted-foreground'}
                      `}>
                        {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span className={`text-xs mt-2 text-center hidden sm:block ${isActive ? 'text-teal-500 font-medium' : 'text-muted-foreground'}`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${isCompleted ? 'bg-teal-500' : 'bg-muted'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <Progress value={(currentStep / steps.length) * 100} className="h-2" />
          </CardContent>
        </Card>

        {/* Main Content */}
        {kycStatus === 'approved' ? (
          <Card className="overflow-hidden">
            <div className="h-2 bg-green-500" />
            <CardContent className="pt-8 text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-bold">Identity Verified</h2>
              <p className="text-muted-foreground">Your account is fully verified with access to all features.</p>
              <div className="bg-muted/50 rounded-lg p-4 max-w-sm mx-auto">
                <ul className="text-sm text-left space-y-1">
                  <li>✓ Unlimited transactions</li>
                  <li>✓ Higher transfer limits</li>
                  <li>✓ Cross-border payments</li>
                  <li>✓ Credit advances</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/dashboard')} className="bg-teal-600 hover:bg-teal-700">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : kycProvider === 'internal' ? (
          /* Internal KYC Flow */
          <Card className="overflow-hidden">
            <div className="h-2 bg-teal-500" />
            <CardHeader>
              <CardTitle>
                {verificationMode === 'enhanced' ? (
                  <>
                    {currentStep === 1 && 'Upload Your ID Document'}
                    {currentStep === 2 && 'Document Fraud Analysis'}
                    {currentStep === 3 && 'Video Liveness Check'}
                    {currentStep === 4 && 'AI Verification in Progress'}
                    {currentStep === 5 && 'Verification Result'}
                  </>
                ) : (
                  <>
                    {currentStep === 1 && 'Upload Your ID Document'}
                    {currentStep === 2 && 'Take a Selfie'}
                    {currentStep === 3 && 'Verifying Your Identity...'}
                    {currentStep === 4 && 'Verification Result'}
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {verificationMode === 'enhanced' ? (
                  <>
                    {currentStep === 1 && 'Take a clear photo of your government-issued ID'}
                    {currentStep === 2 && 'We\'re checking your document for authenticity'}
                    {currentStep === 3 && `Complete the liveness challenge: ${livenessChallenge?.instruction || 'Look at the camera'}`}
                    {currentStep === 4 && 'Our AI is performing comprehensive identity verification'}
                    {currentStep === 5 && 'Review your enhanced verification results'}
                  </>
                ) : (
                  <>
                    {currentStep === 1 && 'Take a clear photo of your government-issued ID'}
                    {currentStep === 2 && 'We need a selfie to match with your ID photo'}
                    {currentStep === 3 && 'Our AI is analyzing your documents'}
                    {currentStep === 4 && 'Review your verification status'}
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: ID Document (same for both modes) */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map(dt => (
                            <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Issuing Country</Label>
                      <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                      ${idImagePreview ? 'border-teal-500 bg-teal-500/5' : 'border-muted-foreground/25 hover:border-teal-500'}`}
                    onClick={() => idInputRef.current?.click()}
                  >
                    {idImagePreview ? (
                      <div className="space-y-4">
                        <img src={idImagePreview} alt="ID Preview" className="max-h-48 mx-auto rounded-lg shadow" />
                        <p className="text-sm text-teal-600">Click to change image</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                        <div>
                          <p className="font-medium">Upload ID Document</p>
                          <p className="text-sm text-muted-foreground">PNG, JPG up to 10MB</p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={idInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleIdImageUpload}
                    />
                  </div>

                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={verificationMode === 'enhanced' ? detectDocumentFraud : extractDocumentData}
                    disabled={!idImage || loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {verificationMode === 'enhanced' ? 'Scan & Check Fraud' : 'Scan Document'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Fraud Analysis Result (Enhanced Mode) */}
              {currentStep === 2 && verificationMode === 'enhanced' && (
                <div className="space-y-4">
                  {fraudResult && (
                    <div className={`rounded-lg p-4 space-y-3 ${fraudResult.is_authentic ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                      <div className="flex items-center gap-2">
                        {fraudResult.is_authentic ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <p className="font-medium">
                          {fraudResult.is_authentic ? 'Document Appears Authentic' : 'Document Issues Detected'}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Risk Score:</span>
                          <span className={`ml-2 font-medium ${fraudResult.risk_score < 0.3 ? 'text-green-600' : fraudResult.risk_score < 0.6 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {(fraudResult.risk_score * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Confidence:</span>
                          <span className="ml-2 font-medium">{(fraudResult.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      
                      {fraudResult.security_features_present?.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Security features detected:</p>
                          <div className="flex flex-wrap gap-1">
                            {fraudResult.security_features_present.map((f, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-500/20 text-green-700 dark:text-green-300 rounded text-xs">{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {fraudResult.tampering_detected && (
                        <div className="text-sm text-red-600">
                          <AlertTriangle className="w-4 h-4 inline mr-1" />
                          Tampering detected in: {fraudResult.tampering_areas?.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={() => { setCurrentStep(3); startCamera(); }}
                    disabled={!fraudResult?.is_authentic}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Continue to Liveness Check
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Selfie (Standard Mode) */}
              {currentStep === 2 && verificationMode === 'standard' && (
                <div className="space-y-4">
                  {/* Extracted Data Preview */}
                  {extractedData && (
                    <div className="bg-teal-500/10 rounded-lg p-4 space-y-2">
                      <p className="font-medium text-teal-700 dark:text-teal-300">Document Data Extracted:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Name:</span> {extractedData.document_data?.full_name}</div>
                        <div><span className="text-muted-foreground">DOB:</span> {extractedData.document_data?.date_of_birth}</div>
                        <div><span className="text-muted-foreground">ID Number:</span> {extractedData.document_data?.document_number}</div>
                        <div><span className="text-muted-foreground">Nationality:</span> {extractedData.document_data?.nationality}</div>
                      </div>
                    </div>
                  )}

                  {cameraActive ? (
                    <div className="space-y-4">
                      <div className="relative rounded-xl overflow-hidden bg-black">
                        <video ref={videoRef} autoPlay playsInline className="w-full" />
                        <div className="absolute inset-0 border-4 border-teal-500/50 rounded-xl pointer-events-none" />
                      </div>
                      <div className="flex gap-4">
                        <Button onClick={capturePhoto} className="flex-1 bg-teal-600 hover:bg-teal-700">
                          <Camera className="w-4 h-4 mr-2" /> Capture
                        </Button>
                        <Button onClick={stopCamera} variant="outline">
                          <X className="w-4 h-4 mr-2" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : selfiePreview ? (
                    <div className="space-y-4">
                      <div className="rounded-xl overflow-hidden bg-muted">
                        <img src={selfiePreview} alt="Selfie Preview" className="w-full max-h-64 object-cover" />
                      </div>
                      <div className="flex gap-4">
                        <Button onClick={startCamera} variant="outline" className="flex-1">
                          <RefreshCw className="w-4 h-4 mr-2" /> Retake
                        </Button>
                        <Button 
                          onClick={performFullVerification} 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          disabled={loading}
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                          Verify Identity
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div 
                        className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-teal-500"
                        onClick={() => selfieInputRef.current?.click()}
                      >
                        <Camera className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="font-medium">Upload or Take a Selfie</p>
                        <p className="text-sm text-muted-foreground">Face the camera clearly</p>
                        <input
                          ref={selfieInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleSelfieUpload}
                        />
                      </div>
                      <Button onClick={startCamera} className="w-full" variant="outline">
                        <Camera className="w-4 h-4 mr-2" /> Use Camera
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Video Liveness (Enhanced Mode) */}
              {currentStep === 3 && verificationMode === 'enhanced' && (
                <div className="space-y-4">
                  {/* Challenge instruction */}
                  {livenessChallenge && (
                    <div className="bg-teal-500/10 rounded-lg p-4 text-center">
                      <Fingerprint className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                      <p className="font-medium text-teal-700 dark:text-teal-300">{livenessChallenge.name}</p>
                      <p className="text-sm text-muted-foreground">{livenessChallenge.instruction}</p>
                      <p className="text-xs text-muted-foreground mt-1">Recording: {livenessChallenge.duration_seconds} seconds</p>
                    </div>
                  )}
                  
                  {/* Video preview */}
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                    {isRecording && (
                      <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        Recording
                      </div>
                    )}
                    <div className="absolute inset-0 border-4 border-teal-500/50 rounded-xl pointer-events-none" />
                    {/* Face guide overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-64 border-2 border-dashed border-white/50 rounded-full" />
                    </div>
                  </div>
                  
                  {/* Progress bar during recording */}
                  {isRecording && (
                    <div className="space-y-2">
                      <Progress value={recordingProgress} className="h-2" />
                      <p className="text-xs text-center text-muted-foreground">
                        {livenessChallenge?.instruction}
                      </p>
                    </div>
                  )}
                  
                  {/* Captured frames indicator */}
                  {videoFrames.length > 0 && !isRecording && (
                    <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {videoFrames.length} frames captured
                    </div>
                  )}
                  
                  {/* Action buttons */}
                  <div className="flex gap-4">
                    {!cameraActive ? (
                      <Button onClick={startCamera} className="flex-1" variant="outline">
                        <Camera className="w-4 h-4 mr-2" /> Start Camera
                      </Button>
                    ) : !isRecording && videoFrames.length === 0 ? (
                      <Button onClick={startVideoRecording} className="flex-1 bg-red-500 hover:bg-red-600">
                        <Video className="w-4 h-4 mr-2" /> Start Recording
                      </Button>
                    ) : !isRecording && videoFrames.length > 0 ? (
                      <>
                        <Button onClick={() => { setVideoFrames([]); startVideoRecording(); }} variant="outline" className="flex-1">
                          <RefreshCw className="w-4 h-4 mr-2" /> Re-record
                        </Button>
                        <Button 
                          onClick={performEnhancedVerification} 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          disabled={loading}
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                          Complete Verification
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Step 3: Processing (Standard Mode) */}
              {currentStep === 3 && verificationMode === 'standard' && (
                <div className="text-center py-8 space-y-4">
                  <Loader2 className="w-16 h-16 animate-spin text-teal-500 mx-auto" />
                  <h3 className="text-lg font-medium">Analyzing Your Documents</h3>
                  <p className="text-muted-foreground">Our AI is verifying your identity...</p>
                  <div className="max-w-xs mx-auto space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Extracting document data
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Analyzing face match
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" /> Liveness detection
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Processing (Enhanced Mode) */}
              {currentStep === 4 && verificationMode === 'enhanced' && (
                <div className="text-center py-8 space-y-4">
                  <Loader2 className="w-16 h-16 animate-spin text-teal-500 mx-auto" />
                  <h3 className="text-lg font-medium">Enhanced Verification in Progress</h3>
                  <p className="text-muted-foreground">Running comprehensive identity checks...</p>
                  <div className="max-w-xs mx-auto space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Document fraud analysis
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Video liveness check
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Face matching
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" /> Final verification
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Result (Standard) / Step 5: Result (Enhanced) */}
              {((currentStep === 4 && verificationMode === 'standard') || (currentStep === 5 && verificationMode === 'enhanced')) && verificationResult && (
                <div className="space-y-4">
                  <div className={`text-center p-6 rounded-xl ${
                    verificationResult.status === 'approved' ? 'bg-green-500/10' :
                    verificationResult.status === 'manual_review' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                  }`}>
                    {getStatusIcon()}
                    <h3 className="text-lg font-medium mt-4">
                      {verificationResult.status === 'approved' && 'Verification Successful!'}
                      {verificationResult.status === 'manual_review' && 'Under Manual Review'}
                      {verificationResult.status === 'rejected' && 'Verification Failed'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      {verificationResult.checks_summary.passed}/{verificationResult.checks_summary.total} checks passed 
                      ({verificationResult.checks_summary.percentage}%)
                    </p>
                  </div>

                  {/* Enhanced Results - Fraud & Liveness Details */}
                  {verificationMode === 'enhanced' && verificationResult.fraud_detection && (
                    <div className="grid grid-cols-2 gap-4">
                      {/* Fraud Detection Summary */}
                      <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-teal-500" />
                          <p className="font-medium text-sm">Document Analysis</p>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Authentic:</span>
                            <span className={verificationResult.fraud_detection.is_authentic ? 'text-green-600' : 'text-red-600'}>
                              {verificationResult.fraud_detection.is_authentic ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tampering:</span>
                            <span className={!verificationResult.fraud_detection.tampering_detected ? 'text-green-600' : 'text-red-600'}>
                              {verificationResult.fraud_detection.tampering_detected ? 'Detected' : 'None'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Risk Score:</span>
                            <span>{(verificationResult.fraud_detection.risk_score * 100).toFixed(0)}%</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Liveness Summary */}
                      <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Video className="w-4 h-4 text-teal-500" />
                          <p className="font-medium text-sm">Liveness Check</p>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Live Person:</span>
                            <span className={verificationResult.video_liveness?.is_live ? 'text-green-600' : 'text-red-600'}>
                              {verificationResult.video_liveness?.is_live ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Blink:</span>
                            <span>{verificationResult.video_liveness?.blink_detected ? '✓' : '✗'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Movement:</span>
                            <span>{verificationResult.video_liveness?.head_movement_detected ? '✓' : '✗'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Spoofing:</span>
                            <span className={!verificationResult.video_liveness?.spoofing_detected ? 'text-green-600' : 'text-red-600'}>
                              {verificationResult.video_liveness?.spoofing_detected ? 'Detected!' : 'None'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Checks Detail */}
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <p className="font-medium text-sm">Verification Checks:</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {Object.entries(verificationResult.checks).map(([key, passed]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-xs">{key.replace(/_/g, ' ')}</span>
                          {passed ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button onClick={resetVerification} variant="outline" className="flex-1">
                      <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                    </Button>
                    <Button onClick={() => navigate('/dashboard')} className="flex-1 bg-teal-600 hover:bg-teal-700">
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Veriff Flow */
          <Card className="overflow-hidden">
            <div className="h-2 bg-teal-500" />
            <CardHeader className="text-center">
              {getStatusIcon()}
              <CardTitle className="mt-4">
                {currentStep === 1 && 'Start Veriff Verification'}
                {currentStep >= 2 && 'Complete Verification in Veriff'}
              </CardTitle>
              <CardDescription>
                {currentStep === 1 && 'Verify your identity through our trusted partner Veriff'}
                {currentStep >= 2 && 'A new window has opened for you to complete verification'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {currentStep === 1 && (
                <div className="max-w-sm mx-auto space-y-4">
                  <div className="space-y-2">
                    <Label>Document Country</Label>
                    <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={startVeriffSession}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Start Verification
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {currentStep >= 2 && (
                <div className="text-center space-y-4">
                  {veriffSession?.session_url && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(veriffSession.session_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Veriff Window
                    </Button>
                  )}
                  
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Waiting for verification completion...</span>
                  </div>
                  
                  <Button variant="outline" onClick={checkVeriffStatus} disabled={checkingStatus}>
                    {checkingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Check Status
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Security Notice */}
        <Card className="bg-muted/30">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-teal-500 flex-shrink-0" />
              <div>
                <h3 className="font-medium mb-1">Secure & Private</h3>
                <p className="text-sm text-muted-foreground">
                  {kycProvider === 'internal' 
                    ? 'Your documents are processed securely by OnePay\'s AI system. We use bank-level encryption and never store raw document images after verification.'
                    : 'Your documents are encrypted and processed securely by Veriff, a trusted identity verification provider.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
